/**
 * AddToCollectionSheet — 담기 시트 (SC-012 · FN-021, design 2026-08-25/08-27 이식).
 *
 * 담을 때 묻는 것은 **수량 하나**다. 종전에는 전체화면 폼(HoldingFormPage)이 떠서
 * 감정사·구매처·보관장소·메모까지 한 번에 물었는데, 기획 §6.1 이 말하는 담기는
 * "스캔 결과에서 원하는 카드만 저장" 하는 한 번의 탭이고 §6.2 는 그 나머지를 *저장
 * **가능한** 정보* 로 둔다.
 * ⚠ 그 나머지 필드는 2026-08-25 에 폐기됐다(FN-022 삭제). 나중에 채우는 값이 아니라
 *   **묻지 않는 값**이고, 수정 화면도 진입점이 없다(2026-09-08).
 *
 * **담기는 두 곳으로 갈린다** (2026-09-17 — IA rev27 을 코드에 맞춘 것).
 *   | target | 쓰는 자리 | 들어가는 곳 |
 *   |---|---|---|
 *   | `holding` | 스캔 결과 | **보유 카드** + (고르면) 컬렉션 |
 *   | `collection` | 홈 트렌딩 · 카드 상세 | **컬렉션만** |
 *
 *   IA rev27 이 "보유는 **스캔으로만** 만들어진다 — 카드 상세에서 컬렉션에 담아도,
 *   수동 추가(SC-016)로 넣어도 보유가 되지 않는다" 로 두 층을 갈라 놓았다. 보유의 정의가
 *   *"카메라에 실물을 댄 적 있다"* 라서다. 종전엔 어느 자리에서 눌러도 보유가 생겨
 *   실물이 없는 카드까지 보유 총액에 들어갔다.
 *   ⚠ `collection` 에서는 묶음 고르기가 **필수**다. 서버가 회원마다 기본 컬렉션
 *     ('즐겨찾기')을 만들어 두므로 고를 것이 없는 상태는 생기지 않는다.
 *   ⚠ `holding` 의 묶음 담기는 **실패해도 담기를 되돌리지 않는다.** 보유는 이미 저장됐고,
 *     분류가 안 된 것뿐이라 전체를 실패로 만들면 사용자가 카드를 잃은 것처럼 보인다.
 *
 * **수량은 '이번에 담을 장수' 다** (2026-09-17 사용자 결정). 2장 있는 카드를 1로 담으면
 *   3장이 된다 — 종전엔 기존 수량을 미리 채워 보여 주고 그 값으로 덮어써서, 같은 카드를
 *   또 스캔해도 장수가 늘지 않았다. 묶음에는 수량이 없다(컬렉션 총액은 **1장 기준**, IA).
 *
 * ⚠ 구매가는 묻지 않는다 (2026-08-25 · FN-022 폐기). 컬렉션이 답하는 질문이
 *   "얼마 벌었나" 에서 "지금 얼마인가" 로 바뀌었다.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@astryxdesign/core/Button";
import { Heading } from "@astryxdesign/core/Heading";
import { IconButton } from "@astryxdesign/core/IconButton";
import { NumberInput } from "@astryxdesign/core/NumberInput";
import { Text } from "@astryxdesign/core/Text";
import { TextInput } from "@astryxdesign/core/TextInput";
import { SelectableCard } from "@astryxdesign/core/SelectableCard";
import { Divider } from "@astryxdesign/core/Divider";
import { VStack, HStack } from "@astryxdesign/core/Layout";
import Icon from "../primitives/Icon";
import Sheet from "../feedback/Sheet";
import { useToast } from "../feedback/Toast";
import { createHolding, updateHolding, type HoldingItem } from "../../api/collection";
import { COLLECTION_CARD_MAX, COLLECTION_MAX, QTY_MAX, clampQty } from "./collectionLimits";
import {
  COLLECTION_NAME_MAX,
  canSubmitCollectionName,
  clampCollectionName,
  collectionNameLength,
} from "./collectionName";
import { addCardToGroup, createCollectionGroup } from "../../api/collectionGroup";
import { collectionGroupName } from "./collectionGroupName";
import { useCollectionQuery, COLLECTION_KEY } from "../../hooks/useCollectionQueries";
import {
  useCollectionGroupsQuery,
  COLLECTION_GROUP_KEY,
} from "../../hooks/useCollectionGroupQueries";
import { useQueryClient } from "@tanstack/react-query";
import styles from "./AddToCollectionSheet.module.css";

interface AddToCollectionSheetProps {
  open: boolean;
  onClose: () => void;
  cardSerialNo: number;
  cardName: string;
  /** 어디에 담는가 — 위 표 참조. 스캔 결과만 `holding` 이다 */
  target: "holding" | "collection";
  /** 담은 뒤 호출부가 추가로 할 일이 있을 때 (스캔 레일 정리 등) */
  onAdded?: (holding: HoldingItem) => void;
}

export default function AddToCollectionSheet({
  open,
  onClose,
  cardSerialNo,
  cardName,
  target,
  onAdded,
}: AddToCollectionSheetProps) {
  const { t } = useTranslation("card");
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data } = useCollectionQuery();

  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  /** 고른 묶음 — null 이면 "묶음에 넣지 않고 담기" (선택 사항이다). */
  const [groupSerialNo, setGroupSerialNo] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const groups = useCollectionGroupsQuery();

  // 이미 보유 중인 카드 — 담기는 새 행을 만들지 않고 이 행의 수량에 더한다.
  // 지금 몇 장인지는 안내 줄이 밝힌다.
  const existing = data?.holdings.find((h) => h.card.cardSerialNo === cardSerialNo);

  useEffect(() => {
    if (!open) return;
    // 수량은 늘 1 에서 시작한다 — '이번에 담을 장수' 이지 '지금 몇 장인가' 가 아니다.
    setQty(1);
    setBusy(false);
    setGroupSerialNo(null);
    setCreating(false);
    setNewName("");
    // 열릴 때 한 번만 맞춘다 — 열려 있는 동안 서버 갱신이 사용자 입력을 덮으면 안 된다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cardSerialNo]);

  const submit = async () => {
    if (busy) return;
    // 담을 곳이 없으면 시작하지 않는다 — try 안에서 돌아서면 busy 가 켜진 채 남는다.
    if (target === "collection" && pickedGroup === null) return;
    setBusy(true);
    try {
      // 컬렉션 담기 — 보유는 만들지 않는다 (IA rev27, 위 표). 여기서는 묶음 실패를
      // 삼키지 않는다: 삼킬 '이미 저장된 보유' 가 없어서, 조용히 넘기면 아무 데도
      // 담기지 않은 채 "담았어요" 가 뜬다.
      if (target === "collection") {
        await addCardToGroup(pickedGroup!, cardSerialNo);
        await queryClient.invalidateQueries({ queryKey: COLLECTION_GROUP_KEY });
        toast.success(t("collection.addSheet.added", { name: cardName }));
        onClose();
        return;
      }

      // 이번에 담을 장수를 기존 보유에 **더한다** (2026-09-17). 999 에서 멈춘다(FN-021).
      const quantity = clampQty((existing?.quantity ?? 0) + clampQty(qty));
      // 보유 중이면 수량만 갱신한다 — 새로 만들면 같은 카드가 두 줄이 된다.
      // ⚠ 나머지 필드는 **기존 값을 그대로 실어 보낸다.** 빼면 서버가 null 로 덮어
      //   사용자가 수정 화면에서 적어 둔 구매처·메모가 담기 한 번에 지워진다.
      const saved = existing
        ? await updateHolding(existing.holdingSerialNo, {
            cardSerialNo,
            quantity,
            purchasePrice: existing.purchasePrice,
            purchaseDate: existing.purchaseDate,
            purchaseSource: existing.purchaseSource,
            currency: existing.currency,
            condition: existing.condition,
            finish: existing.finish,
            pattern: existing.pattern,
            grader: existing.grader,
            grade: existing.grade,
            gradeCertNo: existing.gradeCertNo,
            keepPlace: existing.keepPlace,
            photoAttachFileSerialNo: existing.photoAttachFileSerialNo,
            memo: existing.memo,
          })
        : await createHolding({ cardSerialNo, quantity });
      // ⚠ 묶음 담기는 보유 저장 **뒤에**, 실패해도 삼킨다. 보유는 이미 저장됐으므로
      //   여기서 throw 하면 "담기 실패" 로 보이지만 실제로는 담겨 있다.
      if (pickedGroup !== null) {
        try {
          await addCardToGroup(pickedGroup, cardSerialNo);
          await queryClient.invalidateQueries({ queryKey: COLLECTION_GROUP_KEY });
        } catch {
          toast.error(t("collection.addSheet.groupFailed"));
        }
      }
      await queryClient.invalidateQueries({ queryKey: COLLECTION_KEY });
      toast.success(t("collection.addSheet.added", { name: cardName }));
      onAdded?.(saved);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("holding.saveFailed"));
      setBusy(false);
    }
  };

  const createGroup = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const made = await createCollectionGroup(name);
      await queryClient.invalidateQueries({ queryKey: COLLECTION_GROUP_KEY });
      // 방금 만든 묶음을 곧바로 고른 상태로 둔다 — 만들자마자 다시 고르게 하면 손짓이 하나 는다.
      setGroupSerialNo(made.groupSerialNo);
      setCreating(false);
      setNewName("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("collection.addSheet.groupFailed"));
    } finally {
      setBusy(false);
    }
  };

  const groupList = groups.data ?? [];
  /* 컬렉션 50개 (FN-032) · 컬렉션당 500장 (FN-021·FN-029). 화면에서만 막는다 —
     서버도 같은 값으로 거른다(collectionLimits.ts). */
  const collectionsFull = groupList.length >= COLLECTION_MAX;
  /**
   * 실제로 담을 묶음. 컬렉션 담기에서 아직 아무것도 안 골랐으면 **기본 컬렉션**이다.
   *
   * ⚠ 이 값을 state 초기값으로 넣지 않는다 — 묶음 목록은 시트가 열린 뒤에 도착할 수
   *   있고, 도착할 때마다 state 를 맞추면 그 사이 사용자가 고른 묶음이 되돌려진다.
   */
  const defaultGroupSerialNo = groupList.find((g) => g.defaultGroup)?.groupSerialNo ?? null;
  const pickedGroup = target === "collection"
    ? groupSerialNo ?? defaultGroupSerialNo
    : groupSerialNo;
  const selectedGroup = groupList.find((g) => g.groupSerialNo === pickedGroup);
  const selectedFull = !!selectedGroup && selectedGroup.cardCount >= COLLECTION_CARD_MAX;

  return (
    <Sheet open={open} onClose={onClose} ariaLabel={t("collection.addSheet.title")}>
      <VStack gap={4} padding={4}>
        <VStack gap={1}>
          <Heading level={3}>{t("collection.addSheet.title")}</Heading>
          <Text type="supporting" color="secondary">{cardName}</Text>
        </VStack>

        {target === "holding" && existing && (
          <Text type="supporting" color="secondary">
            {t("collection.addSheet.alreadyHave", { count: existing.quantity })}
          </Text>
        )}

        {/* 묶음 고르기 (SC-012) — **선택 사항**이다. 안 고르면 보유에만 담긴다.
            ⚠ 목록이 길어지면 시트가 밀리므로 여기만 스크롤한다(.groupList). 제목·수량·
              CTA 는 늘 보여야 한다. */}
        {groupList.length > 0 && (
          <VStack gap={2} className={styles.groupList}>
            <Text type="supporting" color="secondary">{t("collection.addSheet.pick")}</Text>
            {groupList.map((g) => (
              <SelectableCard
                key={g.groupSerialNo}
                label={collectionGroupName(g, t)}
                /* 같은 묶음을 다시 누르면 선택 해제 — '안 넣기' 로 돌아갈 길이 있어야 한다.
                   ⚠ 컬렉션 담기에서는 해제하지 않는다. 거기서는 묶음이 담길 곳 자체라
                     '아무 데도 안 넣기' 가 담기를 없애는 것과 같다. */
                isSelected={g.groupSerialNo === pickedGroup}
                onChange={() =>
                  setGroupSerialNo(
                    pickedGroup === g.groupSerialNo && target === "holding"
                      ? null
                      : g.groupSerialNo,
                  )}
                padding={3}
                width="100%"
              >
                <HStack gap={3} align="center" justify="between" width="100%">
                  <Text weight="medium">{collectionGroupName(g, t)}</Text>
                  <Text type="supporting" color="secondary">
                    {t("collection.cardCount", { count: g.cardCount })}
                  </Text>
                </HStack>
              </SelectableCard>
            ))}
          </VStack>
        )}

        {/* 새 묶음 만들기 (SC-013) — ghost + '+'. 면을 두르면 위 묶음 항목들과 같은 무게가
            되어 '고르는 것' 과 '만드는 것' 이 한 줄로 섞여 보인다(design 2026-08-24). */}
        {creating ? (
          <VStack gap={2}>
            {/* 이름 규칙(FN-033): 20자에서 입력이 멈추고 아래에 카운터가 선다.
                앞뒤 공백은 저장 시 trim, 중복은 허용한다. */}
            {/* ⚠ 카운터는 TextInput **밖**의 Astryx Text 다 (2026-09-03). design 원본은
                  description 자리에 넣고 global.css 로 Astryx Field 의 flex 를 grid 로
                  갈아 라벨 줄 오른쪽에 세웠는데, 그 규칙이 Field 내부 DOM
                  (.astryx-field-label + span)을 :has() 로 파고들어 Astryx 를 올리면
                  조용히 깨진다. 입력 아래 오른쪽 정렬이면 지역 CSS 없이 같은 일을 한다. */}
            <TextInput
              label={t("collection.nameLabel")}
              value={newName}
              onChange={(v) => setNewName(clampCollectionName(v))}
              placeholder={t("collection.namePlaceholder")}
              isDisabled={busy}
            />
            <Text type="supporting" display="block" justify="end">
              {t("collection.nameCount", { n: collectionNameLength(newName), max: COLLECTION_NAME_MAX })}
            </Text>
            <HStack gap={2} justify="end">
              <Button
                variant="ghost"
                label={t("collection.cancel")}
                isDisabled={busy}
                clickAction={() => { setCreating(false); setNewName(""); }}
              />
              <Button
                variant="secondary"
                label={t("collection.confirmCreate")}
                isDisabled={busy || !canSubmitCollectionName(newName)}
                clickAction={() => void createGroup()}
              />
            </HStack>
          </VStack>
        ) : (
          <VStack gap={1}>
            <Button
              variant="ghost"
              icon={<Icon name="Add" size="md" />}
              label={t("collection.create")}
              isDisabled={busy || collectionsFull}
              clickAction={() => setCreating(true)}
            />
            {/* 흐리기만 하면 고장처럼 읽혀서 왜 못 누르는지를 적는다 (FN-032). */}
            {collectionsFull && (
              <Text type="supporting" color="secondary">
                {t("collection.limitReached", { max: COLLECTION_MAX })}
              </Text>
            )}
          </VStack>
        )}

        {/* 고른 컬렉션이 가득 찼다 — 담기는 막고 이유를 말한다 (FN-021·FN-029). */}
        {selectedFull && (
          <Text type="supporting" color="secondary">
            {t("collection.addSheet.full", { max: COLLECTION_CARD_MAX })}
          </Text>
        )}

        {/* 수량은 보유에만 있다 — 컬렉션 총액은 **1장 기준** 합이라(IA SC-002 표)
            묶음 매핑에 장수를 받아 둘 자리가 없다. */}
        {target === "holding" && (
        <>
        <Divider />

        {/* 수량 — **스테퍼**다 (design 2026-08-27). 대부분 1~2장을 조정하므로 키보드를
            띄우지 않고 눌러서 올리고 내릴 수 있어야 한다. 숫자 입력도 남긴다(10장씩
            담을 땐 타이핑이 빠르다).
            ⚠ 라벨과 입력이 **한 줄**이다. 위아래로 쌓으면 '수량' 이 아래 CTA 를 여는
              소제목처럼 읽힌다 — 값 하나짜리 줄은 옆으로 놓는 게 맞다.
            ⚠ variant 는 ghost 다. primary·secondary 가 모두 브랜드 오렌지라 스테퍼에
              쓰면 아래 '담기' CTA 와 같은 무게로 보인다. */}
        <HStack gap={2} align="center" justify="between" wrap="wrap">
          <Text type="supporting" color="secondary">{t("collection.addSheet.qty")}</Text>
          <HStack gap={1} align="center">
            <IconButton
              label={t("collection.addSheet.qtyMinus")}
              variant="ghost"
              icon={<Icon name="Minus" size="md" />}
              isDisabled={qty <= 1 || busy}
              clickAction={() => setQty(Math.max(1, qty - 1))}
            />
            <div className={styles.qtyField}>
              <NumberInput
                label={t("collection.addSheet.qty")}
                isLabelHidden
                value={qty}
                min={1}
                isIntegerOnly
                /* ⚠ max 를 주지 않는다 — Astryx NumberInput 은 max 를 넘는 입력을 **무효로 보류**하고
                   onChange 를 안 부른다(design 2026-08-28 실측). 문서(FN-021)는 '초과 입력은 999 로
                   클램프' 라 여기서 받아 clampQty 로 누른다. */
                onChange={(v) => setQty(clampQty(Number(v) || 1))}
              />
            </div>
            <IconButton
              label={t("collection.addSheet.qtyPlus")}
              variant="ghost"
              icon={<Icon name="Add" size="md" />}
              /* 999 에서 '+' 가 죽는다 (FN-021). */
              isDisabled={busy || qty >= QTY_MAX}
              clickAction={() => setQty(clampQty(qty + 1))}
            />
          </HStack>
        </HStack>
        </>
        )}

        <Button
          variant="primary"
          label={t("collection.addSheet.submit")}
          clickAction={() => void submit()}
          /* 컬렉션 담기는 담을 곳이 정해져야 누를 수 있다. 기본 컬렉션이 먼저 도착하므로
             실제로 막히는 구간은 목록이 아직 안 온 짧은 순간뿐이다. */
          isDisabled={busy || selectedFull || (target === "collection" && pickedGroup === null)}
          width="100%"
        />
      </VStack>
    </Sheet>
  );
}
